import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Heart, Moon, Sparkles, Trophy, ArrowRight, Mail } from "lucide-react";

const WomensBalanceGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
            <Mail className="w-4 h-4" />
            <span className="text-sm font-medium">Guide Sent to Your Email</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Your Hormonal Balance Guide
          </h1>
          <p className="text-green-100 text-lg">
            Your foundation is strong. Here's how to maintain beautiful balance for years to come.
          </p>
        </div>
      </div>

      {/* Guide Content Preview */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Balance Maintenance Guide</h2>
              <p className="text-gray-500 text-sm">For Women With Strong Hormonal Foundations</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
            <p className="text-green-800 font-medium">
              🎉 Wonderful news! Your assessment shows your hormonal systems are well-balanced. 
              This guide will help you maintain this harmony as your body changes over time.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Maintenance Strategies:</h3>
            
            <div className="space-y-6">
              {/* Section 1 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Honor Your Cycle
                </h4>
                <p className="text-gray-600 mb-3">
                  Your menstrual cycle is a vital sign. Tracking it helps you stay connected to your body's rhythms.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Track your cycle (app or journal)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Adjust exercise intensity with your cycle phase</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Eat cyclically (more carbs luteal phase, more protein follicular)</span>
                  </li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Protect Your Sleep Sanctuary
                </h4>
                <p className="text-gray-600 mb-3">
                  Sleep is when your hormones reset. Quality sleep = balanced hormones.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Consistent bedtime (within 30 min each night)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Magnesium glycinate before bed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Complete darkness (blackout curtains or eye mask)</span>
                  </li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Nourish Your Nervous System
                </h4>
                <p className="text-gray-600 mb-3">
                  Stress is the #1 disruptor of hormonal balance. Build resilience:
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Daily nervous system reset (5 min breathwork or meditation)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Regular social connection (oxytocin is protective)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>Time in nature weekly (minimum 2 hours)</span>
                  </li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 flex items-center gap-2 mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  Supportive Herbs for Ongoing Balance
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-gray-600">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Red Raspberry Leaf</p>
                    <p className="text-sm">Uterine tonic, cycle support</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Nettle</p>
                    <p className="text-sm">Mineral-rich, gentle detox support</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Holy Basil (Tulsi)</p>
                    <p className="text-sm">Adaptogen for daily stress</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="font-semibold text-gray-900">Shatavari</p>
                    <p className="text-sm">Women's rejuvenative tonic</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Explore Our Herb Encyclopedia</h3>
          <p className="text-green-100 mb-6 max-w-lg mx-auto">
            Learn more about the 100+ herbs that can support your continued hormonal harmony.
          </p>
          <Link to="/herbs">
            <Button className="bg-white text-green-700 hover:bg-green-50 px-8 py-3 text-lg font-semibold rounded-full">
              Browse Herbs <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Back Links */}
        <div className="text-center mt-8 space-x-4">
          <Link to="/womens-assessment" className="text-green-600 hover:text-green-700 font-medium">
            ← Retake Assessment
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-700 font-medium">
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WomensBalanceGuide;
